import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLogo from '../../components/AppLogo';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import repositoryContainer from '../../services/RepositoryContainer';

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s]).{8,}$/;

const getApiErrorMessage = (error) => {
  if (error?.data?.message) return error.data.message;
  if (typeof error?.data === 'string' && error.data.trim()) return error.data;

  const validationErrors = error?.data?.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  return 'This invitation could not be accepted. It may be invalid, expired, or already used.';
};

const AcceptInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token')?.trim() || '', [searchParams]);
  const [form, setForm] = useState({
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    avatarUrl: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(token ? '' : 'This invitation link is missing its secure token.');
  const [isAccepted, setIsAccepted] = useState(false);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!token) {
      setError('This invitation link is missing its secure token.');
      return;
    }

    if (!PASSWORD_PATTERN.test(form.password)) {
      setError('Password must be 8+ characters with uppercase, lowercase, number, and symbol.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await repositoryContainer.staff.acceptInvitation({
        token,
        password: form.password,
        phoneNumber: form.phoneNumber.trim() || null,
        avatarUrl: form.avatarUrl.trim() || null,
      });
      setIsAccepted(true);
    } catch (submissionError) {
      setError(getApiErrorMessage(submissionError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-lg">
        <div className="rounded-3xl border border-border bg-card/90 p-6 shadow-2xl backdrop-blur-sm sm:p-8 md:p-10">
          <div className="mb-8 text-center">
            <div className="mb-4 inline-flex items-center gap-3">
              <AppLogo size={42} shape="rounded" bg="brand" className="shadow-md" />
              <span className="font-heading text-2xl font-bold text-foreground">Claw</span>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground">
              {isAccepted ? 'Welcome to the Team' : 'Accept Your Invitation'}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground font-caption">
              {isAccepted
                ? 'Your account is ready. Sign in to access your workspace.'
                : 'Create your account details to join the company workspace.'}
            </p>
          </div>

          {isAccepted ? (
            <div className="space-y-6 text-center animate-in fade-in zoom-in duration-300">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                <Icon name="CheckCircle" size={34} color="var(--color-success)" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-foreground">Invitation accepted</h2>
                <p className="mt-2 text-sm text-muted-foreground font-caption">
                  Use the email address that received the invitation and the password you just created.
                </p>
              </div>
              <Button fullWidth onClick={() => navigate('/admin/auth')}>
                Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-start gap-3 rounded-xl border border-error/20 bg-error/10 p-4">
                  <Icon name="AlertCircle" size={20} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-error font-caption">{error}</p>
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-medium text-foreground">Password <span className="text-destructive">*</span></label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(event) => updateField('password', event.target.value)}
                    className="pr-10"
                    autoComplete="new-password"
                    required
                    disabled={!token || isSubmitting}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground font-caption">
                  At least 8 characters with uppercase, lowercase, number, and symbol.
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-foreground">Confirm password <span className="text-destructive">*</span></label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={(event) => updateField('confirmPassword', event.target.value)}
                    className="pr-10"
                    autoComplete="new-password"
                    required
                    disabled={!token || isSubmitting}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirmPassword((current) => !current)}
                    aria-label={showConfirmPassword ? 'Hide confirmation password' : 'Show confirmation password'}
                  >
                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
              </div>

              <Input
                type="tel"
                label="Phone number"
                value={form.phoneNumber}
                onChange={(event) => updateField('phoneNumber', event.target.value)}
                placeholder="Optional"
                className="placeholder:text-muted-foreground/60"
                autoComplete="tel"
                disabled={!token || isSubmitting}
              />

              <Input
                type="url"
                label="Profile photo URL"
                value={form.avatarUrl}
                onChange={(event) => updateField('avatarUrl', event.target.value)}
                placeholder="Optional"
                className="placeholder:text-muted-foreground/60"
                autoComplete="url"
                disabled={!token || isSubmitting}
              />

              <Button type="submit" fullWidth loading={isSubmitting} disabled={!token}>
                Accept Invitation
              </Button>

              <button
                type="button"
                className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-primary font-caption"
                onClick={() => navigate('/admin/auth')}
              >
                Back to Sign In
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
